import React, { useState, useContext, useEffect } from 'react';
import { SummaryHeader, SummaryFooter } from '../summary-view.tsx';
import ListDropdown from '../../ui/list-dropdown.tsx';
import { CloudData, DealerDetails } from '../../../data/cloud-data.ts';
import { Configuration } from '../../../data/configuration.ts';
import { AppContext, ViewStates } from '../../app.js';
import DisabledOverlay from '../../ui/disabled-overlay.tsx';
import FormInput, { Input } from '../../ui/form-input.tsx'; 
import { CameraController } from '../../../camera-controller.ts';
import { generatePdfImages } from '../summary-step-two.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

enum Views {
	SendToDealerStepOne,
	SendToDealerStepTwo,
	SendToDealerStepThree,
}

interface SendToDealerViewProps {
	onBackButtonClicked: () => void;
	cameraController: CameraController;
	showHeader?: boolean;
	onComplete: () => void;
}

interface DealerFormProps {
	title: string;
	inputs: Input[];
	handleUpdateInput: (key: string, value: string) => void;
	toggleNextButton: (disabled: boolean) => void;
	inputValues: {
		[key: string]: string;
	};
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function SendToDealerView(props: SendToDealerViewProps) {

	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// states.

	const defaultDealer = { id: '11', name: 'None' };

	const [loading, setLoading] = useState(false);
	const [view, setView] = useState(Views.SendToDealerStepOne);
	const [inputValues, setInputValues] = useState<{
		firstName: string,
		lastName: string,
		email: string,
		tel: string,
		address1: string,
		address2: string,
		address3: string,
		address4: string,
		postcode: string,
		country: string,
	}>({
		firstName: context.globalUserDetails.firstName,
		lastName: context.globalUserDetails.lastName,
		email: context.globalUserDetails.email,
		tel: '',
		address1: '',
		address2: '',
		address3: '',
		address4: '',
		postcode: '',
		country: '',
	});
	const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
	const [dealers, setDealers] = useState([]);
	const [selectedDealer, setSelectedDealer] = useState <DealerDetails>(defaultDealer);
	const [termsChecked, setTermsChecked] = useState<boolean>(false);
	const [optinChecked, setOptinChecked] = useState<boolean>(true);

	// handlers.

	const handleUpdateInput = (inputId: string, value: string) => {

		// store input values in state.
		setInputValues((prevState) => (
			{
				...prevState,
				[inputId]: value
			}
		));

		const globalValues = ['email', 'firstName', 'lastName'];
		if (globalValues.includes(inputId)) {
			context.updateGlobalUserDetails(inputId, value);
		};
	}

	// select the dealer.
	const handleSelectDealer = (dealerId: string) => {
		let filteredDealer: DealerDetails = defaultDealer;
		if (dealerId !== '11')  {
			const dealer = dealers.find((dealer:DealerDetails) => dealer.id === dealerId);
			if (dealer) {
				filteredDealer = dealer;
			}
		}
		setSelectedDealer(filteredDealer);
	}

	// sends data to the server, generates the PDF, then calls the on complete callback to navigate to the summary screen.
	const handleSendToDealer = async () => {

		// set true to disable posting to the server (for debugging purposes).
		const debugSuppressPost = false;

		// disable UI while posting data.
		setLoading(true);

		// save config to get the short code.
		const savedConfig = await CloudData.saveConfiguration();
		if (!savedConfig || savedConfig.shortcode.length === 0) {
			setLoading(false);
			return false;
		}
		const shortCode = savedConfig.shortcode;

		// post the data to the server.
		if (!debugSuppressPost) {
			await CloudData.sendToDealer(
				inputValues,
				savedConfig.shortcode,
				selectedDealer,
				Configuration.sendToDealerLeadId,
				Configuration.totalCost,
				termsChecked,
				optinChecked
			);
		}

		// now generate a PDF...

		// lower roof and doors for PDF images.
		context.lowerRoofAndDoors();

		// generate the pdf images.
		const imageBlobs = await generatePdfImages(props.cameraController);
		if (!imageBlobs || imageBlobs.length === 0) {
			setLoading(false);
			return false;
		}

		// get the dealer info, using default values if no dealer has been selected.
		const dealerSubtitle = selectedDealer.subtitle ? selectedDealer.subtitle : 'Morgan';
		const dealerCompany = selectedDealer.company ? selectedDealer.company : 'Morgan';
		const dealerEmail = selectedDealer.email ? selectedDealer.email : 'contact@morgan-motor.co.uk';

		// generate the PDF, passing additional parameters for dealer info.
		if (!debugSuppressPost) {
			await CloudData.generatePdf(shortCode, imageBlobs, inputValues.email, inputValues.firstName, inputValues.lastName, 'dealer', dealerSubtitle, dealerCompany, dealerEmail);
		}

		// execute the on complete callback.
		props.onComplete();

		// re-enable the UI.
		setLoading(false);
		return true;
	}

	// fetch the list of dealers from the server.
	const fetchDealers = async () => {

		setLoading(true);
		const postcode = inputValues?.postcode;

		const response = await fetch(`https://webapi.morgan-motor.com/getdealers.aspx?address=${postcode}`);
		const data = await response.json();

		setDealers(data);
		setLoading(false);
		return data;
	}

	const handleNextClicked = async() => {
		
		// handle next click.
		if (view === Views.SendToDealerStepOne) {
			setNextButtonDisabled(true);
			setView(Views.SendToDealerStepTwo);
		} else if (view === Views.SendToDealerStepTwo) {
			await fetchDealers();
			setView(Views.SendToDealerStepThree);
		} else if (view === Views.SendToDealerStepThree) {
			handleSendToDealer();
		}
	}

	const handleBackClicked = () => {

		// handle back click.
		if (view === Views.SendToDealerStepTwo) {
			setView(Views.SendToDealerStepOne);
		} else if (view === Views.SendToDealerStepThree) {
			setView(Views.SendToDealerStepTwo);
		} else {
			props.onBackButtonClicked();
		}
	}

	const handleNextButton = (isDisabled:boolean) => {
		setNextButtonDisabled(isDisabled);
	}

	const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, viewState: ViewStates) => {
		e.preventDefault();

		context.handleChangeView(viewState);
	}

	// conditionally render views.
	const currentView = () => {
		switch (view) {
			case Views.SendToDealerStepOne:

				// present the step one view.
				return (
					<DealerForm
						title="DETAILS"
						inputs={[
							{ id: 'firstName', placeholder: 'First Name', required: true, maxLength: 50 },
							{ id: 'lastName', placeholder: 'Last Name', required: true, maxLength: 50 },
							{ id: 'email', placeholder: 'Email', required: true, maxLength: 255 },
							{ id: 'tel', placeholder: 'Phone Number' }
						]}
						handleUpdateInput={handleUpdateInput}
						inputValues={inputValues}
						toggleNextButton={handleNextButton}
					/>
				);

			case Views.SendToDealerStepTwo:

				// present the step two view.
				return (
					<DealerForm
						title="FULL ADDRESS"
						inputs={[
							{ id: 'address1', placeholder: 'Street Address' },
							{ id: 'address2', placeholder: 'Address Line 2' },
							{ id: 'address3', placeholder: 'City' },
							{ id: 'address4', placeholder: 'State / Province / Region' },
							{ id: 'postcode', placeholder: 'Zip / Postal Code', required: true },
							{ id: 'country', placeholder: 'Country', required: true }
						]}
						handleUpdateInput={handleUpdateInput}
						inputValues={inputValues}
						toggleNextButton={handleNextButton}
					/>
				);

			case Views.SendToDealerStepThree:

				// present the step three view.
				return (
					<div>
						<h3>SEND TO DEALER</h3>
						{dealers.length > 0
							? (
								<ListDropdown
									title="SELECT DEALER"
									selectedItem={selectedDealer.id}
									handleItemSelected={(dealerId) => handleSelectDealer(dealerId)}
									listOptions={[
										{ id: '11', name: 'None' },
										...dealers
									]}
								/>
							)
							: (
								<p>There were no dealers found in your area but don't worry, you can still submit your model to us!</p>
							)}
					</div>
				);

			default:
				return null;
		}
	};

	const handleNextButtonText = () => {
		if (view === Views.SendToDealerStepThree) {
			if (selectedDealer.id === '11') {
				return 'SKIP';
			} else {
				return 'SUBMIT';
			}
		} else return 'NEXT';
	}

	// return the HTML.
	return (
		<div className="save-configuration-view summary-container">
			<div>
				{props.showHeader && (
					<SummaryHeader cameraController={props.cameraController} setIsDisabled={() => {}} showPrice={false} />
				)}
				<h2 id="ga4-save-configuration">SAVE CONFIGURATION</h2>
				{currentView()}
			</div>
			<div style={{ marginTop: 'auto' }}>
				{view === Views.SendToDealerStepThree && (
					<div>
						<div className="checkbox-container" style={{ paddingBottom: 8 }}>
							<input type="checkbox" id="tscs" name="tscs" value="tscs" checked={termsChecked} onChange={() => setTermsChecked(!termsChecked)} style={{ width: 40, height: 40 }}/>
							<label
								style={{ color: 'white' }}
								htmlFor="tscs"
							>
								By submiting this form I agree to the <a href="/" style={{ color: '#B78E5F' }} onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleNavigate(e, ViewStates.ImportantInformation)}>Terms & Conditions</a> and <a href="/" style={{ color: '#B78E5F' }} onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => handleNavigate(e, ViewStates.ImportantInformation)}>Privacy Policy</a></label>
						</div>

						<div className="checkbox-container" style={{ paddingBottom: 8 }}>
							<input type="checkbox" id="optin" name="optin" value="optin" checked={optinChecked} onChange={() => setOptinChecked(!optinChecked)} style={{ width: 40, height: 40 }}/>
							<label
								style={{ color: 'white' }}
								htmlFor="optin"
							>
								I wish to Opt-in to communication from Morgan and to be kept in the customer database.</label>
						</div>
					</div>
				)}
				<SummaryFooter
					onNextButtonClicked={handleNextClicked}
					onBackButtonClicked={handleBackClicked}
					nextButtonText={handleNextButtonText()}
					nextButtonDisabled={nextButtonDisabled || (view === Views.SendToDealerStepThree && !termsChecked)}
				/>
			</div>

			{loading && <DisabledOverlay/>}
		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function DealerForm(props: DealerFormProps) {

	// ensure required fields have a value before allowing user to select the next button.
	// destructured here so the effect depends on these specific props rather than the whole props object.
	const { inputs, inputValues, toggleNextButton } = props;
	useEffect(() => {
		const requiredInputs = inputs.filter((input) => input.required);

		let disabled = true;

		// loop around required values and check if they exist.
		for (let i = 0; i < requiredInputs.length; i++) {
			const requiredVal = inputValues[requiredInputs[i].id];

			if (!requiredVal) {
				disabled = true;
				break;
			} else {
				disabled = false;
			}
		}

		// ensure that there are no invalid inputs before the user moves to the next page.
		const inputError = document.getElementById('invalid-input-error');
		if (inputError) {
			disabled = true;
		}

		toggleNextButton(disabled);
	}, [inputs, inputValues, toggleNextButton]);

	// return the HTML.
	return (
		<div >
			<h3>{props.title}</h3>
			<div className="input-container">
				{props.inputs.map((input: Input, index: number) => {
					if (input.id === 'country') {
						return (
							<div key={input.id}>
								<select
									name="country"
									value={props.inputValues[input.id]}
									onChange={(e: React.BaseSyntheticEvent) => props.handleUpdateInput(input.id, e.target.value)}
									required
								>
									<option value="">Select Country *</option>
									<option value="United Kingdom">United Kingdom</option>
									<option value="Australia">Australia</option>
									<option value="Austria">Austria</option>
									<option value="Belgium">Belgium</option>
									<option value="Czech Republic">Czech Republic</option>
									<option value="Denmark">Denmark</option>
									<option value="France">France</option>
									<option value="Germany">Germany</option>
									<option value="Hong Kong">Hong Kong</option>
									<option value="Ireland">Ireland</option>
									<option value="Italy">Italy</option>
									<option value="Japan">Japan</option>
									<option value="Luxembourg">Luxembourg</option>
									<option value="Mexico">Mexico</option>
									<option value="Netherlands">Netherlands</option>
									<option value="Norway">Norway</option>
									<option value="Poland">Poland</option>
									<option value="Portugal">Portugal</option>
									<option value="Spain">Spain</option>
									<option value="Sweden">Sweden</option>
									<option value="Switzerland">Switzerland</option>
									<option value="United States of America">United States of America</option>
									<option value="Other">Other</option>
								</select>
							</div>
						);
					} else {
						return (
							<FormInput
								key={`${input.id}-${index}`}
								handleUpdateInput={props.handleUpdateInput}
								inputValue={props.inputValues[input.id]}
								input={input}
								handleInvalidInput={() => props.toggleNextButton(true)}
							/>
						);
					}
					
				})}
			</div>
		</div>	
	);
}
